<script setup>
import { isEqual, pick } from 'lodash';
import { defineAsyncComponent, ref, watch } from 'vue';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { useBiStore } from '~/bi/store/bi.store';

const draggable = defineAsyncComponent(() => import('vuedraggable'));

const { getIconsForType } = useBIQueryBuilder();
const bi_store = useBiStore();

const $t = inject('$t');

const columns = ref([]);
const rows = ref([]);
const values = ref([]);
const is_dragging = ref(false);
const current_dragged_element = ref(null);

const aggregation_options_map = computed(() => {
  return {
    sum: {
      label: $t('Sum'),
      id: 'sum',
    },
    avg: {
      label: $t('Average'),
      id: 'avg',
    },
    min: {
      label: $t('Minimum'),
      id: 'min',
    },
    max: {
      label: $t('Maximum'),
      id: 'max',
    },
    count: {
      label: $t('Count'),
      id: 'count',
    },
  };
});

const chart_config = computed(() => bi_store.widget_builder_config.chart);
const is_aggregation_enabled = computed(() => {
  return chart_config.value.show_column_totals || chart_config.value.show_row_totals || chart_config.value.show_grand_totals;
});

function onDragEnd() {
  is_dragging.value = true;
  const has_values_changed
    = !isEqual(chart_config.value.pivot_columns, columns.value)
    || !isEqual(chart_config.value.pivot_rows, rows.value)
    || !isEqual(chart_config.value.pivot_values, values.value);

  bi_store.widget_builder_config.chart.pivot_columns = columns.value;
  bi_store.widget_builder_config.chart.pivot_rows = rows.value;
  bi_store.widget_builder_config.chart.pivot_values = values.value;

  bi_store.table_widget_config_change_detector = bi_store.table_widget_config_change_detector || has_values_changed;
  is_dragging.value = false;
}

function onDragStart(evt) {
  current_dragged_element.value = evt.item.__draggable_context?.element || null;
}

function getDropGroup(target_zone) {
  return {
    name: 'fields',
    pull: true,
    put: () => {
      return allowDrop(current_dragged_element.value, target_zone);
    },
  };
}

function allowDrop(element, target_zone) {
  const value = element?.column || element;

  if (!value)
    return false;

  const type = getFieldIconType(value);
  if (target_zone === 'rows' || target_zone === 'columns') {
    return type !== 'function';
  }

  if (target_zone === 'values') {
    return type === 'function';
  }

  return true;
}

function updateTableConfig(key, value) {
  bi_store.widget_builder_config.chart[key] = value;
  bi_store.table_widget_config_change_detector = true;
}

function getFieldIconType(alias) {
  const field = bi_store.alias_to_column_mapping()[alias];
  if (field?.resource_type === 'members') {
    return 'members';
  }
  return field?.is_aggregation ? 'function' : field?.field_type;
}

function getMenuItems(alias) {
  return Object.values(aggregation_options_map.value || {}).map(item => ({
    ...item,
    on_click: () => {
      bi_store.widget_builder_config.chart.pivot_values = chart_config.value.pivot_values.map((val) => {
        if (val.column === alias) {
          return {
            ...val,
            aggregation: item.id,
          };
        }
        return val;
      });
      if (chart_config.value.show_column_totals || chart_config.value.show_row_totals || chart_config.value.show_grand_totals) {
        bi_store.table_widget_config_change_detector = true;
      }
    },
  }));
}

watch(() => pick(bi_store.widget_builder_config.chart, ['pivot_columns', 'pivot_rows', 'pivot_values']), () => {
  if (is_dragging.value)
    return;
  columns.value = bi_store.widget_builder_config.chart?.pivot_columns || [];
  rows.value = bi_store.widget_builder_config.chart?.pivot_rows || [];
  values.value = bi_store.widget_builder_config.chart?.pivot_values || [];
}, { immediate: true, deep: true });
</script>

<template>
  <div class="space-y-6">
    <!-- Columns Section -->
    <div class="">
      <div class="font-semibold mb-3 text-sm">
        {{ $t("Columns") }}
      </div>
      <draggable
        v-model="columns"
        :group="getDropGroup('columns')"
        item-key="key"
        handle=".move"
        class="min-h-[50px] gap-y-1"
        @start="onDragStart"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div class="p-2 rounded-md group hover:bg-gray-50">
            <div class="flex items-center">
              <div class="pr-3 move cursor-move">
                <IconHawkDragIcon class="text-gray-400 w-[12px] h-[12px]" />
              </div>
              <div class="flex items-center">
                <div>
                  <component :is="getIconsForType(getFieldIconType(element))" class="text-gray-600 w-4 h-4" />
                </div>
                <span class="text-sm text-gray-600 pl-2">{{ element }}</span>
              </div>
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <!-- Rows Section -->
    <div class="">
      <div class="font-semibold mb-3 text-sm">
        {{ $t('Rows') }}
      </div>
      <draggable
        v-model="rows"
        :group="getDropGroup('rows')"
        item-key="key"
        handle=".move"
        class="min-h-[50px] gap-y-1"
        @start="onDragStart"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div class="p-2 rounded-md group hover:bg-gray-50">
            <div class="flex items-center">
              <div class="pr-2 move cursor-move">
                <IconHawkDragIcon class="text-gray-400 w-[12px] h-[12px]" />
              </div>
              <div class="flex items-center">
                <div>
                  <component :is="getIconsForType(getFieldIconType(element))" class="text-gray-600 w-4 h-4" />
                </div>
                <span class="text-sm text-gray-600 pl-2">{{ element }}</span>
              </div>
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <div class="border-b border-gray-200 mb-6" />

    <!-- Values Section (internally draggable) -->
    <div class="">
      <div class="font-semibold mb-3 text-sm">
        {{ $t('Values') }}
      </div>
      <draggable
        v-model="values"
        :group="getDropGroup('values')"
        item-key="key"
        handle=".move"
        class="min-h-[50px] gap-y-1"
        @start="onDragStart"
        @end="onDragEnd"
      >
        <template #item="{ element: { column, aggregation } }">
          <div class="p-2 rounded-md group hover:bg-gray-50">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="pr-2 move cursor-move">
                  <IconHawkDragIcon class="text-gray-400 w-[12px] h-[12px]" />
                </div>
                <div class="flex items-center">
                  <div :class="{ 'border-l-4 border-warning-300': bi_store.alias_to_column_mapping()[column]?.is_aggregation }">
                    <component :is="getIconsForType(getFieldIconType(column))" class="text-gray-600 w-4 h-4" />
                  </div>
                  <span class="text-sm text-gray-600 pl-2">{{ column }}</span>
                </div>
              </div>
              <HawkMenu
                v-if="is_aggregation_enabled"
                position="fixed"
                additional_trigger_classes="!ring-0 w-full"
                :items="getMenuItems(column)"
              >
                <template #trigger="{ open }">
                  <div class="flex items-center text-gray-500 gap-x-2">
                    <div class="text-sm">
                      {{ aggregation_options_map[aggregation].label }}
                    </div>
                    <IconHawkChevronDown
                      class="transition-transform"
                      :class="{ 'rotate-180 !visible': open }"
                    />
                  </div>
                </template>
              </HawkMenu>
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <div class="border-b border-gray-200 mb-3" />

    <Vueform size="sm" class="mt-3">
      <div class="col-span-12">
        <div class="flex items-start justify-between mb-4">
          <div>
            <div class="text-sm font-medium text-gray-700">
              {{ $t('Show column totals') }}
            </div>
          </div>
          <ToggleElement
            name="show_column_totals"
            class="ml-2"
            :default="bi_store.widget_builder_config.chart.show_column_totals"
            @change="updateTableConfig('show_column_totals', $event)"
          />
        </div>
        <div class="flex items-start justify-between mb-4">
          <div>
            <div class="text-sm font-medium text-gray-700">
              {{ $t('Show row totals') }}
            </div>
          </div>
          <ToggleElement
            name="show_row_totals"
            class="ml-2"
            :default="bi_store.widget_builder_config.chart.show_row_totals"
            @change="updateTableConfig('show_row_totals', $event)"
          />
        </div>
        <div class="flex items-start justify-between mb-4">
          <div>
            <div class="text-sm font-medium text-gray-700">
              {{ $t('Show grand totals') }}
            </div>
          </div>
          <ToggleElement
            name="show_grand_totals"
            class="ml-2"
            :default="bi_store.widget_builder_config.chart.show_grand_totals"
            @change="updateTableConfig('show_grand_totals', $event)"
          />
        </div>
      </div>
    </Vueform>
  </div>
</template>
